import { useEffect, useState } from "react";
import { Star, Loader2, LogIn } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";

type Props = {
  contentType: "article" | "tool";
  slug: string;
};

type RatingRow = {
  rating: number;
  user_id: string;
  comment: string | null;
  created_at: string;
  status: "approved" | "pending" | "rejected";
  flag_reason: string | null;
};

// Regex to detect any kind of external link / domain (mirrors DB validation)
const LINK_RE =
  /(https?:\/\/|www\.|[a-z0-9-]+\.(com|br|net|org|io|co|me|app|xyz|info|biz|shop|store|tv|online|site|us|uk|de|es|it|fr|ru|ar|mx|pt)(\/|\s|$))/i;
const MAX_COMMENT = 500;

export function RatingStars({ contentType, slug }: Props) {
  const { user, loading: authLoading } = useAuth();
  const [rows, setRows] = useState<RatingRow[]>([]);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState("");
  const [hover, setHover] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [hp, setHp] = useState(""); // honeypot — must stay empty
  const [mountedAt] = useState(() => Date.now());

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("ratings")
      .select("rating, user_id, comment, created_at, status, flag_reason")
      .eq("content_type", contentType)
      .eq("content_slug", slug)
      .order("created_at", { ascending: false });

    const list = (data ?? []) as RatingRow[];
    setRows(list);
    if (user) {
      const mine = list.find((r) => r.user_id === user.id);
      setMyRating(mine?.rating ?? 0);
      setMyComment(mine?.comment ?? "");
    } else {
      setMyRating(0);
      setMyComment("");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType, slug, user?.id]);

  // Only approved ratings count for public avg/count and comments list
  const approved = rows.filter((r) => r.status === "approved");
  const count = approved.length;
  const avg = count ? approved.reduce((s, r) => s + r.rating, 0) / count : 0;
  const comments = approved.filter((r) => r.comment && r.comment.trim().length > 0);
  const myRow = user ? rows.find((r) => r.user_id === user.id) : undefined;

  const submit = async (value: number, commentOverride?: string) => {
    if (!user) return;
    // Honeypot: bots usually fill hidden fields
    if (hp.trim().length > 0) {
      toast.error("Envio bloqueado.");
      return;
    }
    // Minimum interaction time: humans take >1.5s to interact
    if (Date.now() - mountedAt < 1500) {
      toast.error("Aguarde um instante antes de enviar.");
      return;
    }
    const commentRaw = (commentOverride ?? myComment).trim();
    if (commentRaw.length > MAX_COMMENT) {
      toast.error(`Comentário muito longo (máx ${MAX_COMMENT} caracteres).`);
      return;
    }
    if (commentRaw && LINK_RE.test(commentRaw)) {
      toast.error("Não é permitido incluir links no comentário.");
      return;
    }
    setSaving(true);
    const { error } = await supabase.from("ratings").upsert(
      {
        user_id: user.id,
        content_type: contentType,
        content_slug: slug,
        rating: value,
        comment: commentRaw || null,
      },
      { onConflict: "user_id,content_type,content_slug" },
    );
    setSaving(false);
    if (error) {
      const msg = error.message || "";
      if (msg.includes("Limite diário")) toast.error("Limite diário de avaliações atingido.");
      else if (msg.includes("rápido demais"))
        toast.error("Você está enviando avaliações rápido demais. Aguarde um momento.");
      else if (msg.includes("mesmo comentário"))
        toast.error("Você já enviou esse mesmo comentário aqui.");
      else if (msg.includes("CAIXA ALTA")) toast.error("Evite escrever em CAIXA ALTA.");
      else if (msg.includes("link")) toast.error("Não é permitido incluir links no comentário.");
      else if (
        msg.includes("inválida") ||
        msg.includes("longo") ||
        msg.includes("curto") ||
        msg.includes("repetição") ||
        msg.includes("palavras")
      )
        toast.error(msg);
      else toast.error("Erro ao enviar avaliação");
      return;
    }
    toast.success("Avaliação registrada!");
    setMyRating(value);
    load();
  };

  const signIn = async () => {
    setSigningIn(true);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.href,
    });
    if (result.error) {
      setSigningIn(false);
      toast.error("Falha ao entrar com Google.");
    }
    // On success: page redirects (result.redirected) or session is set and useAuth updates.
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-bold text-foreground">Avalie este conteúdo</h3>
          {loading ? (
            <p className="text-xs text-muted-foreground mt-1">Carregando...</p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">
              {count > 0
                ? `Média ${avg.toFixed(1)} de ${count} ${count === 1 ? "avaliação" : "avaliações"}`
                : "Seja o primeiro a avaliar"}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => {
            const filled = (hover || myRating || Math.round(avg)) >= n;
            const interactive = !!user && !saving;
            return (
              <button
                key={n}
                disabled={!interactive}
                onMouseEnter={() => interactive && setHover(n)}
                onMouseLeave={() => setHover(0)}
                onClick={() => interactive && submit(n)}
                className={`p-1 transition ${interactive ? "hover:scale-110 cursor-pointer" : "cursor-default"}`}
                aria-label={`${n} estrela${n > 1 ? "s" : ""}`}
              >
                <Star
                  className={`h-6 w-6 transition ${
                    filled ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"
                  }`}
                />
              </button>
            );
          })}
          {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground ml-1" />}
        </div>
      </div>

      {!authLoading && !user && (
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl bg-muted/40 p-3">
          <p className="text-xs text-muted-foreground">
            Entre com sua conta Google para avaliar. Links externos não são permitidos.
          </p>
          <button
            onClick={signIn}
            disabled={signingIn}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-foreground text-background px-4 py-2 text-sm font-semibold hover:opacity-90 transition disabled:opacity-60"
          >
            {signingIn ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <LogIn className="h-4 w-4" />
            )}
            Entrar com Google
          </button>
        </div>
      )}

      {user && (
        <div className="mt-4 space-y-2">
          {/* Honeypot — invisible to humans, bots tend to fill it */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={hp}
            onChange={(e) => setHp(e.target.value)}
            aria-hidden="true"
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
          />
          <label
            htmlFor="rating-comment"
            className="block text-xs font-medium text-muted-foreground"
          >
            Comentário (opcional) — sem links externos
          </label>
          <textarea
            id="rating-comment"
            value={myComment}
            onChange={(e) => setMyComment(e.target.value.slice(0, MAX_COMMENT))}
            rows={3}
            maxLength={MAX_COMMENT}
            placeholder="Conte como essa ferramenta te ajudou..."
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-y"
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-muted-foreground">
              {myComment.length}/{MAX_COMMENT}
            </span>
            <button
              onClick={() => {
                if (!myRating) {
                  toast.error("Escolha de 1 a 5 estrelas antes de salvar.");
                  return;
                }
                submit(myRating);
              }}
              disabled={saving || !myRating}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 transition disabled:opacity-60"
            >
              {saving && <Loader2 className="h-3 w-3 animate-spin" />}
              Salvar avaliação
            </button>
          </div>
          {myRow?.status === "pending" && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400">
              Sua avaliação está em análise pela moderação ({myRow.flag_reason ?? "revisão"}).
            </p>
          )}
          {myRow?.status === "rejected" && (
            <p className="text-[11px] text-destructive">
              Sua avaliação foi removida pela moderação.
            </p>
          )}
        </div>
      )}

      {comments.length > 0 && (
        <div className="mt-5 border-t border-border pt-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Comentários ({comments.length})
          </p>
          <ul className="space-y-3">
            {comments.slice(0, 10).map((c, i) => (
              <li key={i} className="rounded-lg bg-muted/40 p-3">
                <div className="flex items-center gap-1 mb-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      className={`h-3.5 w-3.5 ${
                        c.rating >= n ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"
                      }`}
                    />
                  ))}
                  <span className="ml-2 text-[11px] text-muted-foreground">
                    {new Date(c.created_at).toLocaleDateString("pt-BR")}
                  </span>
                </div>
                <p className="text-sm text-foreground/90 whitespace-pre-wrap break-words">
                  {c.comment}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
